/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListModelProviderKeysRequest,
  ListModelProviderKeysSecurity,
} from "../models/operations/listmodelproviderkeys.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildModelProviderKeysQuery,
  ModelProviderKeysQueryData,
  prefetchModelProviderKeys,
  queryKeyModelProviderKeys,
} from "./modelProviderKeys.core.js";
export {
  buildModelProviderKeysQuery,
  type ModelProviderKeysQueryData,
  prefetchModelProviderKeys,
  queryKeyModelProviderKeys,
};

export type ModelProviderKeysQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listKeys modelKeys
 *
 * @remarks
 * List the model provider keys configured for a project. Key material is never returned.
 */
export function useModelProviderKeys(
  request?: ListModelProviderKeysRequest | undefined,
  security?: ListModelProviderKeysSecurity | undefined,
  options?: QueryHookOptions<
    ModelProviderKeysQueryData,
    ModelProviderKeysQueryError
  >,
): UseQueryResult<ModelProviderKeysQueryData, ModelProviderKeysQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildModelProviderKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listKeys modelKeys
 *
 * @remarks
 * List the model provider keys configured for a project. Key material is never returned.
 */
export function useModelProviderKeysSuspense(
  request?: ListModelProviderKeysRequest | undefined,
  security?: ListModelProviderKeysSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ModelProviderKeysQueryData,
    ModelProviderKeysQueryError
  >,
): UseSuspenseQueryResult<
  ModelProviderKeysQueryData,
  ModelProviderKeysQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildModelProviderKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setModelProviderKeysData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ModelProviderKeysQueryData,
): ModelProviderKeysQueryData | undefined {
  const key = queryKeyModelProviderKeys(...queryKeyBase);

  return client.setQueryData<ModelProviderKeysQueryData>(key, data);
}

export function invalidateModelProviderKeys(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "modelKeys", "listKeys", ...queryKeyBase],
  });
}

export function invalidateAllModelProviderKeys(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "modelKeys", "listKeys"],
  });
}
